import React, { createContext, useCallback, useContext, useState } from "react";
import { AlertTriangle, CheckCircle2, Info, Loader2 } from "lucide-react";
import { Modale, BoutonSombre } from "./ui";

/* Les questions et les messages de la console, dans le style de la console.
   Plus aucune fenêtre grise du navigateur : `demander()` rend une promesse (true/false)
   et `informer()` affiche un simple message. Avec `option: { libelle, cochee }`, la question
   porte une case (« Prévenir … par e-mail », cochée d'office) : la réponse devient
   `{ option: true|false }`, ou false si l'on renonce. */

const Contexte = createContext(null);

const TONS = {
  danger: { Icon: AlertTriangle, couleur: "#B91C1C", fond: "#FEF2F2", bordure: "#FECACA" },
  attention: { Icon: AlertTriangle, couleur: "#B45309", fond: "#FFFBEB", bordure: "#FDE68A" },
  succes: { Icon: CheckCircle2, couleur: "#15803D", fond: "#F0FDF4", bordure: "#BBF7D0" },
  info: { Icon: Info, couleur: "#1D4ED8", fond: "#EFF6FF", bordure: "#BFDBFE" },
};

export function FournisseurConfirmation({ children }) {
  const [boite, setBoite] = useState(null);
  const [coche, setCoche] = useState(false);

  const demander = useCallback((options) => new Promise((resoudre) => {
    setCoche(!!options?.option?.cochee);
    setBoite({ ...options, mode: "question", resoudre });
  }), []);

  const informer = useCallback((options) => new Promise((resoudre) => {
    setBoite({ ...options, mode: "message", resoudre });
  }), []);

  const repondre = (reponse) => {
    boite?.resoudre?.(reponse);
    setBoite(null);
  };

  const ton = TONS[boite?.ton || (boite?.mode === "message" ? "info" : "attention")];
  const Icon = ton.Icon;

  return (
    <Contexte.Provider value={{ demander, informer }}>
      {children}
      {boite && (
        <Modale titre={boite.titre} onFermer={() => repondre(false)} testid="modale-confirmation">
          <div className="rounded-xl px-4 py-3 flex gap-3 items-start"
            style={{ background: ton.fond, border: `1px solid ${ton.bordure}`, color: ton.couleur }}>
            <Icon size={18} className="shrink-0 mt-0.5" />
            <div className="text-sm whitespace-pre-line" data-testid="confirmation-texte">{boite.texte}</div>
          </div>
          {boite.option && (
            <label className="flex items-center gap-2.5 mt-3 text-sm cursor-pointer select-none" data-testid="confirmation-option">
              <input type="checkbox" className="w-4 h-4 accent-[var(--primary)]" checked={coche} onChange={(e) => setCoche(e.target.checked)} />
              {boite.option.libelle}
            </label>
          )}
          {(boite.points || []).length > 0 && (
            <ul className="text-xs text-[var(--muted)] mt-3 space-y-1" data-testid="confirmation-points">
              {boite.points.map((p, k) => <li key={k}>· {p}</li>)}
            </ul>
          )}
          <div className="flex flex-wrap gap-2 mt-5">
            {boite.mode === "question" && (
              <button className="btn-ghost" onClick={() => repondre(false)} data-testid="confirmation-annuler">
                {boite.annuler || "Annuler"}
              </button>
            )}
            <BoutonSombre onClick={() => repondre(boite.option ? { option: coche } : true)} testid="confirmation-valider">
              {boite.valider || (boite.mode === "message" ? "J'ai compris" : "Confirmer")}
            </BoutonSombre>
          </div>
        </Modale>
      )}
    </Contexte.Provider>
  );
}

export const useConfirmation = () => useContext(Contexte) || {
  demander: async () => true,
  informer: async () => true,
};

export const Patience = () => <Loader2 size={14} className="animate-spin" />;
